import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { When } from 'react-if'
import { UpArrowCircleIcon } from '@/atoms/Icons/UpArrowCircleIcon'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { AsH1, HeadingSM, ParagraphMD } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { SimpleThumbnail } from '@/molecules/Thumbnail'
import { Faq, TheatricalReleaseObject } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { mergeQueryParameters } from '@/utils/UriBuilder'
import { useSafeTrack } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { TheatricalPifFaq } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifFaq'
import { TheatricalPifGoalLine } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifGoalLine'
import { TheatricalPifHowYouCanHelpSection } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifHowYouCanHelpSection'
import { PosterOrTrailer } from '@/views/TicketCheckoutViews/ShowtimesView/components/PosterOrTrailer'

export interface ProjectPIFData {
  name: string
  slug: string
  pifAvatar: string
  pifExplainerVideoUrl: string
  pifExplainerText: string
  logoCloudinaryPath: string
}

interface ViewProps {
  theatricalRelease: TheatricalReleaseObject
}

export const TheatricalPayItForwardView: FC<ViewProps> = ({ theatricalRelease }) => {
  const { t } = useTranslate('pif')
  const track = useSafeTrack()
  const { locale } = useLocale()
  const theatricalSlug = theatricalRelease?.theatricalSlug
  const lastViewTracked = useRef<string>()
  const [loadPlayer, setLoadPlayer] = useState(false)
  const { query } = useSafeRouter()
  const isPifDefaultedOpen = query?.defaultOpen === 'true'
  const [shouldShowPifSection, setShouldShowPifSection] = useState(isPifDefaultedOpen)

  const trailerUrl = useMemo(() => {
    if (!theatricalRelease?.region?.trailerUrl) return ''
    return mergeQueryParameters(theatricalRelease?.region?.trailerUrl, { autoplay: '1' })
  }, [theatricalRelease?.region?.trailerUrl])

  useEffect(() => {
    if (isPifDefaultedOpen) {
      setShouldShowPifSection(true)
    }
  }, [isPifDefaultedOpen])

  const isSpanish = locale === 'es'

  const handleLoadPlayer = () => {
    setLoadPlayer(!loadPlayer)
  }

  useEffect(() => {
    if (lastViewTracked.current === theatricalRelease?.theatricalSlug) return

    track('PIF Project Page Viewed', {
      projectSlug: theatricalRelease?.theatricalSlug,
      projectName: theatricalRelease?.title,
    })
    lastViewTracked.current = theatricalRelease?.theatricalSlug
  }, [track, theatricalRelease?.theatricalSlug, theatricalRelease?.title])

  return (
    <PaddedContainer className="w-full !px-0 py-16 sm:!px-4 sm:py-20 md:!px-6 lg:!px-4 lg:py-28 xl:!px-3">
      <header className="mb-4 hidden sm:block">
        <HeadingSM as={AsH1} className="lg:photon-heading-xl flex flex-wrap gap-x-2 gap-y-1" weight="bold">
          {theatricalRelease?.title}
        </HeadingSM>
      </header>

      {/* DESKTOP */}
      <div className="w-full grid-cols-12 gap-16 lg:grid">
        <div className="col-span-12 lg:col-span-8">
          <section id="pay-it-forward-explainer-video" className="mb-6 lg:mb-16">
            <When
              condition={
                theatricalRelease?.region?.trailerUrl && !loadPlayer && theatricalRelease?.region?.trailerThumbnail
              }
            >
              <SimpleThumbnail
                imageUrl={theatricalRelease?.region?.trailerThumbnail ?? ''}
                onClick={handleLoadPlayer}
                className="lg:rounded-xl"
                showButton
              />
            </When>
            <When condition={!isSpanish && loadPlayer}>
              <div className="lg:overflow-hidden lg:rounded-xl">
                <EmbeddedVideo title="Trailer" url={trailerUrl} />
              </div>
            </When>
            <When condition={isSpanish && loadPlayer}>
              <div className="lg:overflow-hidden lg:rounded-xl lg:border-transparent">
                <PosterOrTrailer posterUrl={theatricalRelease?.horizontalPoster ?? ''} youtubeUrl={trailerUrl} />
              </div>
            </When>
            {!theatricalRelease?.region?.trailerThumbnail && theatricalRelease?.region?.trailerUrl && (
              <div className="lg:overflow-hidden lg:rounded-xl">
                <EmbeddedVideo title="Trailer" url={theatricalRelease?.region?.trailerUrl} />
              </div>
            )}
            {theatricalSlug !== slugs.soundOfHopeTheStoryOfPossumTrot && (
              <TheatricalPifGoalLine
                theatricalRelease={theatricalRelease}
                shouldUseIncrementer
                className="py-6 md:hidden"
              />
            )}
            {theatricalSlug === slugs.soundOfHopeTheStoryOfPossumTrot && (
              <TheatricalPifGoalLine
                theatricalRelease={theatricalRelease}
                shouldUseIncrementer
                className="py-6 md:hidden"
                isPossumTrotExperiment
              />
            )}
          </section>
          {theatricalSlug !== slugs.soundOfHopeTheStoryOfPossumTrot && (
            <div className="hidden md:block">
              <TheatricalPifGoalLine theatricalRelease={theatricalRelease} shouldUseIncrementer className="py-8" />
            </div>
          )}
          {theatricalSlug === slugs.soundOfHopeTheStoryOfPossumTrot && (
            <TheatricalPifGoalLine
              isPossumTrotExperiment
              theatricalRelease={theatricalRelease}
              shouldUseIncrementer
              className="hidden py-8 md:block"
            />
          )}

          <TheatricalPifFaq faqs={theatricalRelease?.region?.faqs as Faq[]} className="hidden lg:block" />
        </div>
        <div className="px-4 sm:px-0 lg:col-span-4 lg:block">
          <TheatricalPifHowYouCanHelpSection
            theatricalRelease={theatricalRelease}
            shouldShowPifSection={shouldShowPifSection}
            setShouldShowPifSection={setShouldShowPifSection}
          />
          <TheatricalPifFaq faqs={theatricalRelease?.region?.faqs as Faq[]} className="mt-16 lg:hidden" />
        </div>
      </div>

      <div className="mt-14 px-4 sm:px-0 lg:hidden">
        <a href="#" className="mx-auto flex flex-col items-center justify-center">
          <UpArrowCircleIcon />
          <ParagraphMD color="core-gray-500">{t('backToTop', 'Back to top')}</ParagraphMD>
        </a>
      </div>
    </PaddedContainer>
  )
}
